<template>
    <div class="timeline-item" :class="position">
        <div class="timeline-content">
            <div class="timeline-card">
                <h5 class="role-title">{{ title }}</h5>
                <h6 class="company-name">{{ description }}</h6>
                <div class="meta-info">
                    <span class="date">
                        <i class="fa fa-calendar"></i> {{ date }}
                    </span>
                    <span class="location">
                        <i class="fa fa-map-marker"></i> {{ location }}
                    </span>
                </div>
                <div v-if="achievements && achievements.length > 0" class="achievements-section">
                    <p class="achievements-text">{{ achievements[0] }}</p>
                </div>
            </div>
        </div>
        <div class="timeline-marker" :class="'marker-' + cls">
            <div class="marker-dot"></div>
        </div>
    </div>
</template>

<script>
export default {
    name: 'TimeLineItemVertical',
    props: {
        date: String,
        title: String,
        description: String,
        location: String,
        cls: String,
        achievements: Array,
        position: {
            type: String,
            default: 'left'
        }
    }
}
</script>

<style scoped>
.timeline-item {
    display: flex;
    position: relative;
    margin-bottom: 60px;
    align-items: center;
}

.timeline-item:last-child {
    margin-bottom: 0;
}

/* Posicionamiento para items a la izquierda */
.timeline-item.left {
    justify-content: flex-start;
}

.timeline-item.left .timeline-content {
    order: 1;
    padding-right: 40px;
    text-align: right;
}

.timeline-item.left .timeline-marker {
    order: 2;
}

/* Posicionamiento para items a la derecha */
.timeline-item.right {
    justify-content: flex-end;
}

.timeline-item.right .timeline-content {
    order: 2;
    padding-left: 40px;
    text-align: left;
}

.timeline-item.right .timeline-marker {
    order: 1;
}

.timeline-content {
    flex: 1;
    max-width: calc(50% - 30px);
    z-index: 1;
}

.timeline-card {
    background: #1a1d29;
    border: 1px solid rgba(255, 255, 255, 0.08);
    border-radius: 16px;
    padding: 30px;
    transition: all 0.3s ease;
    position: relative;
}

.timeline-card::before {
    content: '';
    position: absolute;
    top: 50%;
    width: 20px;
    height: 20px;
    background: #1a1d29;
    border: 1px solid rgba(255, 255, 255, 0.08);
    transform: translateY(-50%) rotate(45deg);
}

.timeline-item.left .timeline-card::before {
    right: -11px;
    border-left: none;
    border-bottom: none;
}

.timeline-item.right .timeline-card::before {
    left: -11px;
    border-right: none;
    border-top: none;
}

.timeline-card:hover {
    background: #212430;
    border-color: rgba(255, 107, 107, 0.3);
    box-shadow: 0 8px 32px rgba(0, 0, 0, 0.4);
    transform: translateY(-5px);
}

.timeline-card:hover::before {
    background: #212430;
}

.timeline-marker {
    position: relative;
    width: 30px;
    height: 30px;
    flex-shrink: 0;
    z-index: 2;
}

.marker-dot {
    width: 24px;
    height: 24px;
    border-radius: 50%;
    background: #ff6b6b;
    border: 4px solid #1a1a2e;
    box-shadow: 0 0 0 4px #482d36;
    transition: all 0.3s ease;
    position: relative;
}

.marker-primary .marker-dot {
    background: #ff6b6b;
    box-shadow: 0 0 0 4px rgba(255, 107, 107, 0.3),
                0 0 20px rgba(255, 107, 107, 0.4);
    animation: pulse 2s ease-in-out infinite;
}

@keyframes pulse {
    0%, 100% {
        box-shadow: 0 0 0 4px rgba(255, 107, 107, 0.3),
                    0 0 20px rgba(255, 107, 107, 0.4);
    }
    50% {
        box-shadow: 0 0 0 8px rgba(255, 107, 107, 0.2),
                    0 0 30px rgba(255, 107, 107, 0.6);
    }
}

.role-title {
    font-size: 22px;
    font-weight: 700;
    color: #ffffff;
    margin: 0 0 10px 0;
    line-height: 1.3;
}

.company-name {
    font-size: 18px;
    font-weight: 600;
    color: #ff6b6b;
    margin: 0 0 15px 0;
}

.meta-info {
    display: flex;
    gap: 20px;
    margin-bottom: 15px;
    flex-wrap: wrap;
}

.timeline-item.left .meta-info {
    justify-content: flex-end;
}

.timeline-item.right .meta-info {
    justify-content: flex-start;
}

.meta-info span {
    font-size: 14px;
    color: rgba(255, 255, 255, 0.6);
    display: inline-flex;
    align-items: center;
    gap: 8px;
}

.meta-info i {
    color: #ff6b6b;
    font-size: 13px;
}

.achievements-section {
    margin-top: 15px;
    padding-top: 15px;
    border-top: 1px solid rgba(255, 255, 255, 0.08);
}

.achievements-text {
    font-size: 15px;
    line-height: 1.7;
    color: rgba(255, 255, 255, 0.7);
    margin: 0;
}

/* Responsive */
/* Tablet/móvil: cada experiencia es una tarjeta del carrusel horizontal (ver TimeLineVertical) */
@media (max-width: 968px) {
    .timeline-item,
    .timeline-item.left,
    .timeline-item.right {
        flex: 0 0 85%;
        max-width: 420px;
        margin-bottom: 0;
        align-items: stretch;
        scroll-snap-align: start;
    }

    .timeline-item.left .timeline-content,
    .timeline-item.right .timeline-content {
        max-width: none;
        padding: 0;
        text-align: left;
    }

    .timeline-marker,
    .timeline-card::before {
        display: none;
    }

    .timeline-card {
        height: 100%;
        box-sizing: border-box;
        padding: 25px;
    }

    .timeline-card:hover {
        transform: none;
    }

    .role-title {
        font-size: 19px;
    }

    .company-name {
        font-size: 16px;
    }

    .timeline-item.left .meta-info,
    .timeline-item.right .meta-info {
        justify-content: flex-start;
        gap: 8px 16px;
    }
}
</style>
